import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import apiClient from '../services/apiClient';

const MockAuth = () => {
  const [mockUsers, setMockUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const { user, checkAuthStatus } = useAuth();
  const userData = user?.data;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      fetchMockUsers();
    }
  }, []);

  // Only show in development
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  const fetchMockUsers = async () => {
    try {
      const response = await apiClient.get('/api/mock-auth/users');
      setMockUsers(response.data.data.users);
    } catch (error) {
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
      setError('Failed to load mock users');
    }
  };

  const handleMockLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      await apiClient.post('/api/mock-auth/login', {}, {
        params: { mockUserId: selectedUserId }
      });

      // After successful mock login, refresh the auth status to get the current user
      await checkAuthStatus();

      setError(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
      setError(error.response?.data?.message || 'Mock login failed');
    } finally {
      setLoading(false);
    }
  };

  const testSlackIntegration = async () => {
    try {
      const response = await apiClient.get('/api/mock-auth/test-slack', {
        params: { mockUserId: selectedUserId }
      });
      
      alert(`Slack test result:\n${JSON.stringify(response.data.data, null, 2)}`);
    } catch (error) {
      alert(`Slack test failed: ${error.response?.data?.message || error.message}`);
    }
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          fontSize: '20px',
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 9999,
        }}
        title="Show Mock Authentication"
      >
        🧪
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      background: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '15px',
      minWidth: '300px',
      maxWidth: '350px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      zIndex: 9999,
      fontSize: '14px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h4 style={{ margin: 0, color: '#495057' }}>
          🧪 Mock Authentication (Dev Only)
        </h4>
        <button
          onClick={() => setIsVisible(false)}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#6c757d',
            padding: '0',
            width: '20px',
            height: '20px',
          }}
          title="Hide Mock Authentication"
        >
          ×
        </button>
      </div>
      
      {userData && (
        <div style={{
          marginBottom: '15px',
          padding: '10px',
          background: '#d4edda',
          borderRadius: '4px',
          border: '1px solid #c3e6cb'
        }}>
          <strong>Current User:</strong><br />
          {userData.name} ({userData.role})<br />
          <small>{userData.email}</small>
        </div>
      )}

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Select Mock User:
        </label>
        <select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            marginBottom: '10px'
          }}
        >
          {mockUsers.map(user => (
            <option key={user.id} value={user.id}>
              {user.name} - {user.role} ({user.email})
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
        <button
          onClick={handleMockLogin}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Logging in...' : 'Mock Login'}
        </button>

        {user && (
          <button
            onClick={testSlackIntegration}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Test Slack Integration
          </button>
        )}
      </div>

      {error && (
        <div style={{
          marginTop: '10px',
          padding: '8px',
          background: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          {error}
        </div>
      )}

      <div style={{ 
        marginTop: '15px', 
        fontSize: '12px', 
        color: '#6c757d',
        borderTop: '1px solid #dee2e6',
        paddingTop: '10px'
      }}>
        <strong>Usage:</strong><br />
        • Select a user and click "Mock Login"<br />
        • Test different roles and permissions<br />
        • Use "Test Slack" to verify notifications<br />
        <br />
        <strong>Available Roles:</strong><br />
        • Admin: Full access<br />
        • Manager: Can approve rules<br />
        • User: Can create rules
      </div>
    </div>
  );
};

export default MockAuth;
